import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { getCreditUsageState } from '~/components/CreditUsageMeter/utils';
import { type EnhancedSubscription } from '~/providers/SubscriptionProvider';

export const getCreditUsageBannerAlertCandidate = ({
  creditStats,
  subscription,
  canEditPlan,
  showPlansModal,
}: {
  creditStats: CreditUsageStats;
  subscription: EnhancedSubscription;
  canEditPlan: boolean;
  showPlansModal: () => void;
}) => {
  if (!creditStats.allocated) {
    return null;
  }

  let candidate:
    | ({ useSessionStorage?: boolean } & {
        title: string;
        description: string;
        ctaButton?: { text: string } & ({ onClick: () => void } | { href: string; newTab?: boolean });
      })
    | null = null;

  const usageState = getCreditUsageState({ allocated: creditStats.allocated, used: creditStats.used });

  switch (usageState) {
    case 'depleted': {
      if (!canEditPlan && subscription.pricingModel !== 'credit') {
        return null;
      }

      candidate = {
        useSessionStorage: canEditPlan,
        title:
          subscription.pricingModel === 'credit'
            ? 'Your workspace is out of credits, so uploading and AI are currently paused.'
            : 'Your workspace is out of credits.',
        description: canEditPlan
          ? 'Upgrade now to keep your work flowing.'
          : 'Contact your admin to upgrade and keep things moving.',
        ctaButton: {
          text: 'Upgrade Plan',
          onClick: showPlansModal,
        },
      };
      break;
    }

    case '90%': {
      let description =
        subscription.pricingModel === 'credit'
          ? 'Upgrade now to keep your work flowing without interruption.'
          : 'Upgrade now to unlock more and keep your work flowing.';

      if (!canEditPlan) {
        description =
          subscription.pricingModel === 'credit'
            ? 'Contact your admin to keep your work flowing without interruption.'
            : 'Contact your admin to keep your work flowing.';
      }

      candidate = {
        title: 'Your workspace is nearly out of credits.',
        description,
        ctaButton: {
          text: 'Upgrade Plan',
          onClick: showPlansModal,
        },
      };
      break;
    }
    default:
      return null;
  }

  if (!canEditPlan) {
    candidate.ctaButton = undefined;
  } else if (subscription.isEnterprise || subscription.isOnBusinessPlan) {
    candidate.ctaButton = {
      text: 'Talk to Sales',
      onClick: showPlansModal,
    };
  }

  return {
    ...candidate,
    creditUsageState: usageState,
  };
};
